import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/common/Header';
import { formatTime12, formatDate } from '../../utils/helpers';

export default function BookingSuccessPage() {
  const { state }  = useLocation();
  const navigate   = useNavigate();
  const booking    = state?.booking;
  const doctor     = state?.doctor;
  const slot       = state?.slot;
  const type       = state?.type || 'clinic';

  if (!booking) {
    navigate('/appointments');
    return null;
  }

  const handleDownloadQR = () => {
    const img = document.getElementById('booking-qr-img');
    if (!img) return;
    const a      = document.createElement('a');
    a.href       = img.src;
    a.download   = `FARISTAA-QR-${booking.bookingId}.png`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Booking Confirmed" showBack={false} />

      <div className="pt-14 px-4 pb-10">

        {/* All steps done indicator */}
        <div className="flex items-center px-6 py-4 bg-white border-b border-border mb-4">
          {['Details','Payment','Done'].map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center shadow-md shadow-green-200">
                  <i className="fa-solid fa-check text-white text-sm" />
                </div>
                <span className="text-[9px] font-bold text-green-600 uppercase tracking-wide">{label}</span>
              </div>
              {i < 2 && <div className="flex-1 h-0.5 mx-2 rounded-full bg-green-400" />}
            </React.Fragment>
          ))}
        </div>

        {/* Success animation banner */}
        <div className="bg-white rounded-3xl border border-border p-6 text-center mb-4 shadow-sm">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fa-solid fa-circle-check text-green-500 text-4xl" />
          </div>
          <h1 className="text-xl font-black text-text-primary font-heading mb-1">Booking Confirmed!</h1>
          <p className="text-sm text-text-muted">
            Your appointment with <span className="font-semibold text-primary">{doctor?.name}</span> is all set
          </p>
        </div>

        {/* Token + booking details */}
        <div className="bg-white rounded-3xl border border-border overflow-hidden mb-4 shadow-sm">
          {/* Token number - big */}
          <div className="bg-primary px-6 py-5 text-center">
            <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-1">Your Token Number</p>
            <p className="text-6xl font-black text-white font-heading leading-none">{booking.tokenNumber}</p>
          </div>

          {/* Details grid */}
          <div className="px-4 py-4 grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-[10px] text-text-muted font-semibold uppercase tracking-wide mb-1">Date</p>
              <p className="text-sm font-bold text-text-primary">{formatDate(booking.date || slot?.date)}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-[10px] text-text-muted font-semibold uppercase tracking-wide mb-1">Time</p>
              <p className="text-sm font-bold text-text-primary">{formatTime12(booking.slotTime || slot?.startTime)}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-[10px] text-text-muted font-semibold uppercase tracking-wide mb-1">Type</p>
              <p className={`text-sm font-bold ${type === 'video' ? 'text-blue-600' : 'text-primary'}`}>
                <i className={`fa-solid ${type === 'video' ? 'fa-video' : 'fa-hospital'} mr-1 text-xs`} />
                {type === 'video' ? 'Video Consult' : 'Clinic Visit'}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-[10px] text-text-muted font-semibold uppercase tracking-wide mb-1">Doctor</p>
              <p className="text-sm font-bold text-text-primary truncate">{doctor?.name}</p>
            </div>
          </div>

          {/* Booking ID — clinic only, not video */}
          {type === 'clinic' && booking.bookingId && (
          <div className="px-4 pb-4">
            <div className="bg-slate-100 rounded-xl px-3 py-2">
              <p className="text-[10px] text-text-muted mb-0.5">Booking ID <span className="text-primary font-semibold">(Show at clinic)</span></p>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs font-mono text-text-primary break-all">{booking.bookingId}</p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(booking.bookingId);
                    import('react-hot-toast').then(m => m.default.success('Booking ID copied!'));
                  }}
                  className="flex-shrink-0 w-7 h-7 bg-primary/10 hover:bg-primary/20 rounded-lg flex items-center justify-center transition-colors"
                  title="Copy Booking ID">
                  <i className="fa-regular fa-copy text-primary text-xs" />
                </button>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* QR Code — clinic only */}
        {type === 'clinic' && booking.qrImageBase64 && (
          <div className="bg-white rounded-3xl border border-border p-5 text-center mb-4 shadow-sm">
            <div className="flex items-center justify-center gap-2 mb-3">
              <i className="fa-solid fa-qrcode text-primary" />
              <p className="text-sm font-bold text-text-primary">Show QR at Clinic</p>
            </div>
            <div className="inline-block p-3 bg-white border-2 border-primary/20 rounded-2xl shadow-sm">
              <img
                id="booking-qr-img"
                src={booking.qrImageBase64}
                alt="Booking QR"
                className="w-48 h-48"
              />
            </div>
            <p className="text-[11px] text-text-muted mt-2 mb-3">
              One-time use only · Expires at midnight
            </p>
            <button onClick={handleDownloadQR}
              className="flex items-center gap-2 mx-auto text-sm text-primary font-bold">
              <i className="fa-solid fa-download" />Download QR Code
            </button>
          </div>
        )}

        {/* Video consult — go to chat */}
        {type === 'video' && (
          <div className="bg-blue-50 border border-blue-200 rounded-3xl p-5 text-center mb-4">
            <i className="fa-solid fa-video text-blue-600 text-2xl mb-2" />
            <p className="text-sm font-bold text-blue-800 mb-1">Video Consultation</p>
            <p className="text-xs text-blue-600 mb-4">
              The chat will open only during your slot time. You can view history anytime.
            </p>
            <button
              onClick={() => navigate('/appointments')}
              className="w-full py-3 bg-blue-600 text-white rounded-2xl text-sm font-bold">
              <i className="fa-solid fa-calendar-check mr-2" />View in My Appointments
            </button>
          </div>
        )}

        {/* Info: review after visit */}
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3 mb-4">
          <i className="fa-solid fa-star text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-700 leading-relaxed">
            After your appointment is <strong>completed</strong>, you can rate this doctor from <strong>My Appointments</strong>.
          </p>
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          <button onClick={() => navigate('/appointments')}
            className="w-full py-4 rounded-2xl bg-primary text-white font-bold text-base shadow-lg shadow-primary/20 active:scale-98 transition-all">
            <i className="fa-solid fa-calendar-check mr-2" />View My Appointments
          </button>
          <button onClick={() => navigate('/search')}
            className="w-full py-3 rounded-2xl bg-green-600 text-white font-bold text-sm active:scale-98 transition-all">
            <i className="fa-solid fa-plus mr-2" />Book Another Appointment
          </button>
          <button onClick={() => navigate('/', { replace: true })}
            className="w-full py-3 rounded-2xl border-2 border-border text-text-secondary font-semibold text-sm">
            <i className="fa-solid fa-house mr-2" />Back to Home
          </button>
        </div>
      </div>


    </div>
  );
}
